import { PaginatedResultModel } from '@data-layer/models/common';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { IPaymentBreakdownItemGroup } from '@interfaces/payment-breakdown-group';
import { IPaymentBreakdownItem } from '@interfaces/payment-breakdown-item';
import { BillItemModel, BillModel } from '@data-layer/models/payments';
import { precisionRound } from '@helpers/data-transform.helper';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';

export function groupCollectionFees<T, M>(data: PaginatedResultModel<T>): PaginatedResultModel<T> {
  (data?.items || []).forEach((item: T) => {
    // #8798 group collection item fees

    const nonCollectionFeeItems: M[] =
      (item?.['items'] || []).filter((paymentItem: M) => ![enumBillItemProductCode.COLLECTION_FEE].includes(paymentItem['productCode'])) ||
      [];
    const collectionFeeItems: M[] =
      (item?.['items'] || []).filter((paymentItem: M) => [enumBillItemProductCode.COLLECTION_FEE].includes(paymentItem['productCode'])) ||
      [];

    item['items'] = [...nonCollectionFeeItems];
    if (collectionFeeItems?.length) {
      item['items'].push({
        ...collectionFeeItems[0],
        quantity: 1,
        collectionFeeItems,
        price: collectionFeeItems.reduce((acc: number, curr: M) => acc + curr['price'], 0),
        ...(collectionFeeItems[0]['billItemDetails'] && {
          billItemDetails: {
            ...collectionFeeItems[0]['billItemDetails'],
            productDetails: {
              ...collectionFeeItems[0]['billItemDetails'].productDetails,
              vendorPrice: collectionFeeItems.reduce(
                (acc: number, curr: M) => acc + (curr['billItemDetails']?.productDetails?.vendorPrice || 0),
                0,
              ),
            },
          },
        }),
      });
    }
  });

  return data;
}

export function filterFeeItems(paymentBreakdown: IPaymentBreakdownItemGroup[]): void {
  paymentBreakdown.forEach((item: IPaymentBreakdownItemGroup) => {
    const nonCollectionFeeItems: IPaymentBreakdownItem[] = item.items.filter(
      (paymentItem: IPaymentBreakdownItem) => ![enumBillItemProductCode.COLLECTION_FEE].includes(paymentItem.code),
    );
    const collectionFeeItems: IPaymentBreakdownItem[] =
      item.items.filter((paymentItem: IPaymentBreakdownItem) => paymentItem.code === enumBillItemProductCode.COLLECTION_FEE) || [];

    item.items = [...nonCollectionFeeItems];
    if (collectionFeeItems?.length) {
      item.items.push({
        ...collectionFeeItems[0],
        quantity: 1,
        price: collectionFeeItems.reduce((acc: number, curr: IPaymentBreakdownItem) => acc + curr.price, 0),
      });
    }
  });
}

/** A digital-POA purchase bill — a standalone payment that no entity page should treat as its own. */
export function isPoaBill(bill: BillModel): boolean {
  return (bill?.items || []).some((item: BillItemModel) => item.productCode === enumBillItemProductCode.POA);
}

export function filterFeeBillItems(paymentBreakdown: BillModel[]): void {
  paymentBreakdown
    .filter((item: BillModel) => item.providerType === enumCompanyPaymentType.INSTANT)
    .forEach((item: BillModel) => {
      const nonCollectionFeeItems: BillItemModel[] = item.items
        .filter((paymentItem: BillItemModel) => paymentItem.productCode !== enumBillItemProductCode.COLLECTION_FEE)
        .map((item: BillItemModel) => ({ ...item, price: precisionRound(item.price) }));
      const collectionFeeItems: BillItemModel[] =
        item.items.filter((paymentItem: BillItemModel) => paymentItem.productCode === enumBillItemProductCode.COLLECTION_FEE) || [];

      item.items = [...nonCollectionFeeItems];
      if (collectionFeeItems?.length) {
        item.items.push({
          ...collectionFeeItems[0],
          quantity: 1,
          price: precisionRound(collectionFeeItems.reduce((acc: number, curr: BillItemModel) => acc + curr.price, 0)),
        });
      }
    });
}
